import type { PlatformCommitOptions } from '../../config/types.ts';
import type { GitOptions } from '../../types/git.ts';
import type { LongCommitSha } from '../schema-utils/git.ts';
import type { EmailAddress } from '../schema-utils/index.ts';

export type { DiffResult, StatusResult } from 'simple-git';

export interface GitAuthor {
  name?: string | null;
  address?: string;
}

export type GitNoVerifyOption = 'commit' | 'push';

/**
 * Represents a virtual branch tracked as `refs/remotes/origin/<name>`.
 * Used by platforms like Gerrit where changes are represented as refs
 * (e.g., refs/changes/34/1234/1) instead of regular branches.
 */
export interface VirtualBranch {
  /** The ref this virtual branch is fetched from (e.g., 'refs/changes/34/1234/1') */
  ref: string;
  /** The commit SHA this virtual branch points to */
  sha: LongCommitSha;
}

export interface StorageConfig {
  currentBranch?: string;
  defaultBranch?: string;
  url: string;
  upstreamUrl?: string | undefined;
  extraCloneOpts?: GitOptions;
  cloneSubmodules?: boolean;
  cloneSubmodulesFilter?: string[];
  fullClone?: boolean;
  /**
   * Virtual branches to initialize from non-standard refs (e.g., Gerrit change refs).
   * Each virtual branch is fetched and stored as refs/remotes/origin/<name>.
   * Keyed by branch name.
   */
  virtualBranches?: Record<string, VirtualBranch>;
}

export interface LocalConfig extends StorageConfig {
  additionalBranches: string[];
  currentBranch: string;
  currentBranchSha: LongCommitSha;
  branchCommits: Record<string, LongCommitSha>;
  /** Single registry of virtual branches, always initialized in initRepo. Keyed by branch name. */
  virtualBranches: Record<string, VirtualBranch>;
  branchIsModified: Record<string, boolean>;
  commitBranches: Record<string, string[]>;
  ignoredAuthors: string[];
  gitAuthorName?: string | null;
  gitAuthorEmail?: EmailAddress;
  fileModeEnabled?: boolean;

  writeGitDone?: boolean;
}

export interface FileAddition {
  /**
   * Addition creates new file or modifies existing one
   */
  type: 'addition';

  /**
   * Relative file path
   */
  path: string;

  /**
   * File contents
   */
  contents: string | Buffer | null;

  /**
   * The executable bit
   */
  isExecutable?: boolean;

  isSymlink?: boolean;
}

export interface FileDeletion {
  /**
   * Deletion removes the file
   */
  type: 'deletion';

  /**
   * Relative file path
   */
  path: string;
}

export type FileChange = FileAddition | FileDeletion;

export interface CommitFilesConfig {
  baseBranch?: string;
  branchName: string;
  files: FileChange[];
  message: string | string[];
  /** Structured git trailers (`Key: value` lines) to add in the final block of the commit message */
  trailers?: string[];
  force?: boolean;
  platformCommit?: PlatformCommitOptions;
  /** Only needed by Gerrit platform */
  prTitle?: string;
  /** Only needed by Gerrit platform */
  autoApprove?: boolean;
}

export interface PushFilesConfig {
  sourceRef: string;
  targetRef?: string;
  files: FileChange[];
  pushOptions?: string[];
}

export type BranchName = string;

export interface CommitResult {
  parentCommitSha: LongCommitSha;
  commitSha: LongCommitSha;
  files: FileChange[];
}

export type GitObjectType = 'blob' | 'tree' | 'commit';

/**
 * Git tree entry modes (octal file-type representations).
 * @see https://git-scm.com/docs/gitdatamodel
 */
export const GitTreeMode = {
  /** Regular non-executable file */
  RegularFile: '100644',
  /** Regular executable file */
  ExecutableFile: '100755',
  /** Symbolic link */
  SymbolicLink: '120000',
  /** Directory / subtree */
  Directory: '040000',
  /** Gitlink (submodule) */
  Gitlink: '160000',
} as const;

export interface DiffTreeItem {
  path: string;
  mode: string;
  type: GitObjectType;
  sha: LongCommitSha | null;
}

/**
 * Represents a git authentication rule in the form of e.g.:
 * git config --global url."https://api@github.com/".insteadOf "https://github.com/"
 */
export interface AuthenticationRule {
  url: string;
  insteadOf: string;
}

export type GitOperationType =
  /**
   * The `git clone` sub-command.
   */
  | 'clone'
  /**
   * The `git reset` sub-command.
   */
  | 'reset'
  /**
   * The `git checkout` sub-command.
   */
  | 'checkout'
  /**
   * The `git fetch` sub-command.
   */
  | 'fetch'
  /**
   * The `git pull` sub-command.
   */
  | 'pull'
  /**
   * The `git push` sub-command.
   */
  | 'push'
  /**
   * The `git clean` sub-command.
   */
  | 'clean'
  /**
   * The `git merge` sub-command.
   */
  | 'merge'
  /**
   * The `git submodule` sub-command.
   */
  | 'submodule'
  /**
   * The `git commit` sub-command.
   */
  | 'commit'
  /**
   * The `git branch` sub-command.
   */
  | 'branch'
  /**
   * Any internal "plumbing" commands
   *
   * - `git update-index`
   *
   * See also: https://git-scm.com/book/en/v2/Git-Internals-Plumbing-and-Porcelain
   */
  | 'plumbing'
  /**
   * Any other operations i.e.
   *
   * - `git add`
   * - `git branch`
   * - `git config`
   * - `git diff`
   * - `git log`
   * - `git ls-remote`
   * - `git remote`
   * - `git rev-parse`
   * - `git status`
   *
   * See also: https://git-scm.com/book/en/v2/Git-Internals-Plumbing-and-Porcelain
   */
  | 'other';
